import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate del Monaco L2-5 (manuale V1, rr. 4544-4665). 16 mosse.
 *
 * Pattern notabili:
 * - **`prerequisites.anyOf` cross-class**: *Mastro Artigiano* richiede
 *   *Regola del Lavoro* OR `mago.laboratorio` (manuale r. 4651). Pattern già
 *   supportato (FI-19 chiuso al Guerriero).
 * - **3 mosse richiedono *Meditazione*** (Pace Interiore, Anima e Corpo,
 *   Quiete Inscalfibile, Proiezione della Volontà, Imperturbabile = 5 mosse).
 * - **Mosse legate alle Regole**: *Stile senz'Armi* (Battaglia), *Atto
 *   Compassionevole* (Pietà), *Mastro Speziale* (Lavoro).
 * - **Mossa multiclasse**: *La Via dell'Eremita* — la classe da cui pescare
 *   dipende dalla Regola scelta dal PG (e, per archetipo Umano, anche da
 *   Druido/Sciamano). Resta description-only (FI-17, regola runtime sullo
 *   stato del PG).
 * - **Catene di prerequisiti**: *Regole Stringenti* → *Regole Ferree*;
 *   *Mastro Speziale* → (*Speziale Senza Pari* in L6-10); *Mastro Artigiano*
 *   → (*Artigiano Senza Pari* in L6-10); *Amanuense* → (*Conoscenza
 *   Enciclopedica* in L6-10).
 */

const regoleStringenti: Move = {
  id: 'monaco.regole-stringenti',
  name: 'Regole Stringenti',
  type: 'advanced_2_5',
  classId: 'monaco',
  trigger: 'Quando prendi questa mossa al level-up',
  description: 'Scegli una *Regola* del Monaco che non ti sei già dato.',
  tags: [],
  prerequisites: [],
  replaces: [],
  // FI-31 (D5): concede una Regola residua del gruppo del Monaco (3 Regole).
  grantsMoveChoice: { kind: 'from-group', groupId: 'regola' },
};

const regoleFerree: Move = {
  id: 'monaco.regole-ferree',
  name: 'Regole Ferree',
  type: 'advanced_2_5',
  classId: 'monaco',
  trigger: 'Quando prendi questa mossa al level-up',
  description: "Scegli l'ultima *Regola* del Monaco che non ti sei già dato.",
  tags: [],
  prerequisites: ['monaco.regole-stringenti'],
  replaces: [],
  // FI-31 (D5): catena 2-step dopo *Regole Stringenti* — pool=1 residuo.
  grantsMoveChoice: { kind: 'from-group', groupId: 'regola' },
};

const laViaDellEremita: Move = {
  id: 'monaco.la-via-dell-eremita',
  name: "La Via dell'Eremita",
  type: 'advanced_2_5',
  classId: 'monaco',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Scegli una mossa non multiclasse del Guerriero o del Ladro (se hai *Regola della Battaglia*), del Bardo o del Mago (se hai *Regola del Lavoro*), oppure del Chierico o del Paladino (se hai *Regola della Pietà*).\n\n**NB**: con archetipo Umano, puoi sempre scegliere anche tra le mosse del Druido o dello Sciamano.',
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-37 + FI-43 (2026-05-15, manuale V1 r. 4563 + r. 4464): pool base **vuoto**
  // (richiede kind 'from-extensions-only'), riempito dalla Regola scelta come
  // mossa starting + archetipo Umano. Le 3 Regole sono mutuamente esclusive
  // (`monacoStartingChoiceMoves` con pickCount: 1).
  multiclass: {
    picks: { kind: 'from-extensions-only' },
    extendsByArchetype: [{ archetypeId: 'umano', extraClasses: ['druido', 'sciamano'] }],
    extendsByMove: [
      { moveId: 'monaco.regola-della-battaglia', extraClasses: ['guerriero', 'ladro'] },
      { moveId: 'monaco.regola-del-lavoro', extraClasses: ['bardo', 'mago'] },
      { moveId: 'monaco.regola-della-pieta', extraClasses: ['chierico', 'paladino'] },
    ],
  },
};

const paceInteriore: Move = {
  id: 'monaco.pace-interiore',
  name: 'Pace Interiore',
  type: 'advanced_2_5',
  classId: 'monaco',
  trigger: 'Quando usi la mossa *Meditazione*',
  description:
    'Prendi 4 e il tuo massimo numero di prese diventa 4. Puoi anche decidere di prendere solo 2 e di rimuovere una delle Debilitazioni che hai segnato.',
  tags: [],
  prerequisites: ['monaco.meditazione'],
  replaces: [],
};

const animaECorpo: Move = {
  id: 'monaco.anima-e-corpo',
  name: 'Anima e Corpo',
  type: 'advanced_2_5',
  classId: 'monaco',
  trigger: 'Quando sottrai energie al tuo corpo per dare sollievo alla tua anima',
  description:
    'Subisci una Debilitazione a tua scelta e guadagni 3 prese di *Meditazione*. Non puoi usare questa mossa se hai già tutte le Debilitazioni segnate, e non puoi superare il numero massimo di prese di *Meditazione*.',
  tags: [],
  prerequisites: ['monaco.meditazione'],
  replaces: [],
};

const stileSenzArmi: Move = {
  id: 'monaco.stile-senz-armi',
  name: "Stile senz'Armi",
  type: 'advanced_2_5',
  classId: 'monaco',
  trigger:
    'Quando *tagli e spacchi* a mani nude o con un bastone, oppure usi la mossa *Proiezione della Volontà*',
  description:
    'Con un 10+, puoi anche, a tua scelta, spostare il tuo avversario da qualche parte entro la tua portata, farlo cadere o bloccarlo sul posto.',
  tags: [],
  prerequisites: ['monaco.regola-della-battaglia'],
  replaces: [],
};

const quieteInscalfibile: Move = {
  id: 'monaco.quiete-inscalfibile',
  name: 'Quiete Inscalfibile',
  type: 'advanced_2_5',
  classId: 'monaco',
  trigger: 'Se non indossi armature e non imbracci uno scudo',
  description:
    'La tua determinazione ti protegge. Finché hai almeno una presa di *Meditazione*, hai 2 Armatura, altrimenti hai comunque 1 Armatura.',
  tags: [],
  prerequisites: ['monaco.meditazione'],
  replaces: [],
};

const proiezioneDellaVolonta: Move = {
  id: 'monaco.proiezione-della-volonta',
  name: 'Proiezione della Volontà',
  type: 'advanced_2_5',
  classId: 'monaco',
  trigger:
    'Quando fai ricorso alla tua ferrea volontà per danneggiare un avversario che stai toccando',
  description:
    "Se non indossi un'armatura, non imbracci uno scudo, e hai abbastanza prese di *Meditazione*, scegli uno o più dei seguenti effetti (al massimo una volta ciascuno):\n\n- infliggere +1d6 danni (1 presa);\n- applicare l'etichetta *impatto* all'attacco (1 presa);\n- stordire il tuo bersaglio, se può essere stordito (1 presa).\n\nTira+**SAG**. Con 10+, infliggi il tuo dado di Danno e applichi tutti gli effetti voluti (ma non le etichette dell'arma che impugni). Con 7-9, lo stesso, ma spendi le prese relative a tutte le opzioni che hai scelto. Con 6-, spendi le prese, ma non colpisci il bersaglio e ne subisci la reazione. Se non scegli nessuna opzione o non soddisfi qualche altro requisito di questa mossa, usa le normali regole di *taglia e spacca*.",
  roll: {
    stat: 'saggezza',
    on10plus: {
      text: "Infliggi il tuo dado di Danno e applichi tutti gli effetti voluti (ma non le etichette dell'arma che impugni).",
    },
    on7to9: {
      text: 'Lo stesso del 10+, ma spendi le prese relative a tutte le opzioni scelte.',
    },
    on6minus: {
      text: 'Spendi le prese, ma non colpisci il bersaglio e ne subisci la reazione.',
    },
  },
  tags: [],
  prerequisites: ['monaco.meditazione'],
  replaces: [],
};

const imperturbabile: Move = {
  id: 'monaco.imperturbabile',
  name: 'Imperturbabile',
  type: 'advanced_2_5',
  classId: 'monaco',
  trigger: 'Quando *sfidi il pericolo* o *difendi*',
  description:
    'Puoi spendere 2 prese di *Meditazione* per ottenere un risultato di 12+, senza dover tirare.',
  tags: [],
  prerequisites: ['monaco.meditazione'],
  replaces: [],
};

const attoCompassionevole: Move = {
  id: 'monaco.atto-compassionevole',
  name: 'Atto Compassionevole',
  type: 'advanced_2_5',
  classId: 'monaco',
  trigger: 'Quando guarisci qualcun altro, non importa come',
  description:
    "Curi +1d4 PF e tu ottieni +1 al prossimo tiro. Inoltre, se spendi 2 prese nell'opzione di cura di *Forza di Volontà*, anche le emorragie vengono arrestate.",
  tags: [],
  prerequisites: ['monaco.regola-della-pieta'],
  replaces: [],
};

const nonviolenza: Move = {
  id: 'monaco.nonviolenza',
  name: 'Nonviolenza',
  type: 'advanced_2_5',
  classId: 'monaco',
  trigger: 'Quando vuoi calmare una situazione agitata parlando o predicando',
  description:
    "Tira+**CAR**. Con un 7-9, per qualche prezioso momento nessuno dei presenti può commettere atti violenti. Con un 10+, l'effetto perdura fintanto che continui a parlare e, inoltre, qualunque personaggio giocante decida di abbandonare la situazione pacificamente segna un PE (al massimo una volta per sessione).",
  roll: {
    stat: 'carisma',
    on10plus: {
      text: "L'effetto perdura fintanto che continui a parlare; ogni PG che abbandona pacificamente segna 1 PE (max 1/sessione).",
    },
    on7to9: {
      text: 'Per qualche prezioso momento nessuno dei presenti può commettere atti violenti.',
    },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const saggioEColui: Move = {
  id: 'monaco.saggio-e-colui',
  name: 'Saggio è Colui...',
  type: 'advanced_2_5',
  classId: 'monaco',
  trigger:
    'Quando fermi le sconsiderate azioni di qualcuno (PG o PNG) consigliando una saggia e valida alternativa',
  description:
    'Tira+**SAG**. Con 10+, riconosce la tua saggezza e seguirà i tuoi consigli al meglio delle sue possibilità. Con 7-9, segue i tuoi consigli, ma a modo suo o solo in parte. Con 6-, ti vede come un ostacolo tra lui e i suoi scopi.',
  roll: {
    stat: 'saggezza',
    on10plus: {
      text: 'Riconosce la tua saggezza e seguirà i tuoi consigli al meglio delle sue possibilità.',
    },
    on7to9: { text: 'Segue i tuoi consigli, ma a modo suo o solo in parte.' },
    on6minus: { text: 'Ti vede come un ostacolo tra lui e i suoi scopi.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const amanuense: Move = {
  id: 'monaco.amanuense',
  name: 'Amanuense',
  type: 'advanced_2_5',
  classId: 'monaco',
  trigger:
    'Quando *declami conoscenze* sfruttando un utilizzo della Borsa di libri; quando ti *accampi* e trascrivi i tuoi appunti',
  description:
    "Quando **declami conoscenze sfruttando un utilizzo della Borsa di libri**, con 10+, non spendi l'utilizzo.\n\nInoltre, quando **ti *accampi* e trascrivi i tuoi appunti**, tira+**INT**. Con 10+, recuperi 1 utilizzo della Borsa di libri (al massimo ne può avere 5). Con 7-9, recuperi comunque 1 utilizzo, ma il GM ti dirà una preoccupazione o un dubbio inquietante sollevato dalla rilettura dei tuoi appunti. Con 6-, ottieni solo dubbi e incertezze.\n\nSe hai la mossa *Regola del Lavoro* e non la usi quando ti *accampi*, ottieni un 10+ per recuperare utilizzi della Borsa di libri con *Amanuense*, senza dover tirare.",
  roll: {
    stat: 'intelligenza',
    on10plus: { text: 'Recuperi 1 utilizzo della Borsa di libri (al massimo 5).' },
    on7to9: {
      text: 'Recuperi 1 utilizzo, ma il GM ti dirà una preoccupazione o un dubbio inquietante.',
    },
    on6minus: { text: 'Ottieni solo dubbi e incertezze.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const mastroSpeziale: Move = {
  id: 'monaco.mastro-speziale',
  name: 'Mastro Speziale',
  type: 'advanced_2_5',
  classId: 'monaco',
  trigger: 'Quando hai qualche ora di tempo e un posto sicuro',
  description:
    "Puoi miscelare un preparato galenico per curare qualcuno da un'afflizione o una debolezza, ripristinarne le energie, oppure migliorarne le capacità. Descrivi quale effetto vorresti che avesse, e quali ingredienti intendi utilizzare. Il GM ti darà da 1 a 4 delle seguenti limitazioni, in relazione alla potenza degli effetti descritti e alla qualità degli ingredienti proposti:\n\n- gli effetti del preparato sono ritardati e si verificheranno solo dopo qualche tempo da che è stato assunto;\n- gli effetti del preparato svaniscono rapidamente: chi lo assume dovrà agire in fretta per poterli sfruttare;\n- il preparato ha effetti secondari strani e indesiderati (a scelta del GM);\n- il preparato ha l'etichetta *pericoloso*: è volatile ed esploderà se fatto cadere o se trattato senza riguardo;\n- il preparato ha un odore e un sapore orribili: chi lo assume subisce un –1 al prossimo tiro;\n- la miscelazione è complessa e hai bisogno di lavorarci per un giorno intero;\n- ti serve anche un altro ingrediente (il GM ti dice quale).\n\nI preparati hanno 1 utilizzo e peso 1. Puoi anche decidere di miscelare un preparato con 3 utilizzi e peso 1, ma il GM ti darà una limitazione in più dalla lista. Se accetti le limitazioni, ottieni il preparato che volevi. Se misceli di nuovo un preparato che hai già ottenuto in precedenza, ti occorreranno lo stesso tempo e gli stessi ingredienti, e avrai le medesime limitazioni.",
  tags: [],
  prerequisites: ['monaco.regola-del-lavoro'],
  replaces: [],
};

const mastroArtigiano: Move = {
  id: 'monaco.mastro-artigiano',
  name: 'Mastro Artigiano',
  type: 'advanced_2_5',
  classId: 'monaco',
  trigger: 'Quando hai la tranquillità necessaria per fabbricare un oggetto mondano',
  description:
    "Spiega qual è l'oggetto base che intendi realizzare (una spada, uno scudo, un anello, ecc.). Il GM ti dirà delle etichette (positive, negative e narrative) che definiscono le peculiarità di tale oggetto, quanto tempo ti occorre (minuti, ore, giorni, ecc.), di che materiali hai bisogno (e il loro costo) e che attrezzatura devi utilizzare. Per ogni opzione che scegli dalla lista seguente (anche la stessa più volte, salvo che non sia indicato diversamente), puoi aggiungere un'etichetta a tua scelta, rimuoverne una tra quelle indicate inizialmente dal GM, dimezzare il tempo (ridurlo a un quarto con 2 opzioni, a un ottavo con 3, ecc.), ridurre della metà i materiali necessari (o a un quarto con 2 opzioni, ecc.), oppure arrangiarti con attrezzatura meno idonea:\n\n- ci vuole molto tempo (ogni volta che scegli questa opzione il tempo base raddoppia);\n- sfrutti le eccellenti dotazioni di un laboratorio artigianale perfettamente attrezzato (un tuo *Monastero* o il *Laboratorio* del Mago lo sono sempre!) Puoi scegliere questa opzione una sola volta;\n- usi un materiale o un semilavorato raro (descrivi quale);\n- ti servono più materiali base (ogni volta che scegli questa opzione il costo raddoppia);\n- ti fai aiutare da qualcuno (descrivi da chi e come);\n- l'oggetto ha un difetto o una limitazione (il GM indica una nuova etichetta appropriata);\n- la fabbricazione causa un incidente imprevisto (il GM ti dice con quali conseguenze, ma solo quando sarà troppo tardi!).\n\nSe il GM approva le tue scelte, puoi costruire l'oggetto che volevi.",
  tags: [],
  prerequisites: [
    {
      // Manuale r. 4651: "Richiede *Regola del Lavoro* o *Laboratorio*".
      // *Laboratorio* è una mossa del Mago — cross-class anyOf (FI-19).
      anyOf: ['monaco.regola-del-lavoro', 'mago.laboratorio'],
    },
  ],
  replaces: [],
};

const memoriaEidetica: Move = {
  id: 'monaco.memoria-eidetica',
  name: 'Memoria Eidetica',
  type: 'advanced_2_5',
  classId: 'monaco',
  trigger:
    'Quando *discerni la realtà* su una persona, un luogo o un oggetto che hai già visto prima',
  description:
    'Puoi sempre ricordare in modo dettagliato qualunque immagine che tu abbia visto almeno una volta (un volto, un simbolo, una mappa, ecc.). Inoltre, su una persona/luogo/oggetto già visto, ottieni +1 al tiro.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

export const monacoAdvanced2to5Moves: readonly Move[] = [
  regoleStringenti,
  regoleFerree,
  laViaDellEremita,
  paceInteriore,
  animaECorpo,
  stileSenzArmi,
  quieteInscalfibile,
  proiezioneDellaVolonta,
  imperturbabile,
  attoCompassionevole,
  nonviolenza,
  saggioEColui,
  amanuense,
  mastroSpeziale,
  mastroArtigiano,
  memoriaEidetica,
] as const;
